/**
 * Donut — répartitions plateforme / langue.
 *
 * Réservé aux répartitions à PEU de parts (deux plateformes, deux langues) :
 * au-delà de cinq secteurs, l'œil ne compare plus des angles et une barre
 * horizontale redevient supérieure. C'est pourquoi la page Utilisateurs met les
 * versions d'app en barres et seulement plateforme/langue en donut.
 */

import { useState } from 'react';
import { ChartFrame, Legend, describeCategorical } from './chartUtils';
import type { Categorical } from './chartUtils';
import { formatInt, formatPercent } from '../lib/format';

export interface DonutProps {
  title: string;
  rows: Categorical[];
  /** Libellé affiché au centre (par défaut : le total). */
  centerLabel?: string;
  emptyLabel?: string;
}

const SIZE = 220;
const R_OUT = 92;
const R_IN = 58;

/** Secteur annulaire en coordonnées SVG. */
function arcPath(startAngle: number, endAngle: number): string {
  const c = SIZE / 2;
  const large = endAngle - startAngle > Math.PI ? 1 : 0;
  const p = (r: number, a: number): [number, number] => [c + r * Math.cos(a), c + r * Math.sin(a)];
  const [x1, y1] = p(R_OUT, startAngle);
  const [x2, y2] = p(R_OUT, endAngle);
  const [x3, y3] = p(R_IN, endAngle);
  const [x4, y4] = p(R_IN, startAngle);
  return [
    `M${x1.toFixed(2)},${y1.toFixed(2)}`,
    `A${R_OUT},${R_OUT} 0 ${large} 1 ${x2.toFixed(2)},${y2.toFixed(2)}`,
    `L${x3.toFixed(2)},${y3.toFixed(2)}`,
    `A${R_IN},${R_IN} 0 ${large} 0 ${x4.toFixed(2)},${y4.toFixed(2)}`,
    'Z',
  ].join(' ');
}

export function Donut(props: DonutProps): React.ReactElement {
  const { title, rows, centerLabel, emptyLabel } = props;
  const [hover, setHover] = useState<number | null>(null);

  const total = rows.reduce((acc, r) => acc + r.value, 0);
  const hovered = hover === null ? undefined : rows[hover];

  // On démarre à midi : un secteur qui commence à 3 h se lit moins bien.
  let angle = -Math.PI / 2;
  const arcs = rows.map((row) => {
    const sweep = total > 0 ? (row.value / total) * Math.PI * 2 : 0;
    const seg = { row, start: angle, end: angle + sweep };
    angle += sweep;
    return seg;
  });

  return (
    <ChartFrame
      ariaLabel={describeCategorical(title, rows)}
      empty={rows.length === 0 || total === 0}
      emptyLabel={emptyLabel}
      legend={<Legend items={rows} />}
      onPointerLeave={() => setHover(null)}
    >
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={describeCategorical(title, rows)}
        style={{ maxWidth: 260, margin: '0 auto' }}
      >
        {arcs.map((arc, i) => (
          <path
            key={arc.row.key}
            d={arcPath(arc.start, arc.end === arc.start ? arc.start + 0.0001 : arc.end)}
            fill={arc.row.color}
            opacity={hover === null || hover === i ? 1 : 0.45}
            stroke="var(--c-surface)"
            strokeWidth={2}
            onPointerEnter={() => setHover(i)}
          />
        ))}
        <text
          className="donut-center"
          x={SIZE / 2}
          y={SIZE / 2 - 2}
          textAnchor="middle"
          style={{ fontSize: 22 }}
        >
          {hovered ? formatPercent(total > 0 ? hovered.value / total : 0) : formatInt(total)}
        </text>
        <text
          x={SIZE / 2}
          y={SIZE / 2 + 18}
          textAnchor="middle"
          className="axis-label"
          style={{ letterSpacing: '0.1em' }}
        >
          {hovered ? hovered.label.toUpperCase() : (centerLabel ?? 'TOTAL')}
        </text>
      </svg>
    </ChartFrame>
  );
}
